import { Vue, prop, ExtractDefaultProps } from 'vue-class-component';
import clsx from 'clsx';
import { Icon, IconNames } from '@/components/icon';
import { computed, ref, watch } from 'vue';
import uniqueId from 'lodash/uniqueId';
import { Button, ButtonTypes } from '@/components/button';
import styles from './styles.module.scss';

class InputWrapperProps {
	icon = prop<IconNames>({ default: undefined })

	error = prop<string>({ default: undefined })

	valid = prop<boolean>({ default: undefined })

	inline = prop<boolean>({ default: false })

	label = prop<string>({ default: undefined })

	id = prop<string>({ default: undefined })

	prefix = prop<string>({ default: undefined })

	isTextOnRight = prop<boolean>({ default: false })

	clearable = prop<boolean>({ default: false })

	onClear = prop<Function>({ default: () => () => undefined })
}

export class BaseInputProps extends InputWrapperProps {
	placeholder = prop<string>({ default: '' })

	disabled = prop<boolean>({ default: false })

	lazy = prop<boolean>({ default: false })

	onBlur = prop<Function>({ default: () => () => undefined })
}

export class BaseTextInputProps extends BaseInputProps {
	'onUpdate:modelValue' = prop<Function>({ default: () => () => undefined })

	modelValue = prop<string | number>({ default: '' })
}

export function useInput(props: Partial<ExtractDefaultProps<BaseTextInputProps>>) {
	const input = ref(props.modelValue);
	const showValid = computed(() => props.valid ?? (props.error === undefined && !!props.modelValue));

	watch(() => props.modelValue, value => {
		input.value = value;
	});

	function emitValue() {
		if (props['onUpdate:modelValue']) {
			props['onUpdate:modelValue'](input.value);
		}
	}

	function onInput(e: Event) {
		const { value } = (e.target as HTMLInputElement);

		input.value = value;
		if (props.modelValue || props.error || !props.lazy) { emitValue(); }
	}

	function onBlur() {
		emitValue();
		if (props.onBlur) {
			props.onBlur();
		}
	}

	return {
		input,
		showValid,
		emitValue,
		onInput,
		onBlur,
		id: uniqueId('precise-price-input-'),
	};
}

export class InputWrapper extends Vue.with(InputWrapperProps) {
	get isError() {
		return this.error !== undefined;
	}

	render() {
		return (
			<div class={ clsx([
				styles.inputWrapper,
				this.isError && styles.inputWrapperError,
				this.valid && styles.inputWrapperValid,
				this.icon && styles.inputWrapperWithIcon,
				this.inline && styles.inputWrapperInline,
				this.isTextOnRight && styles.inputWrapperWithInvertedValue,
				this.prefix && styles.inputWrapperWithPrefix,
			]) }>
				{ this.label
					&& <label class={ styles.inputWrapperLabel } for={ this.id }>{ this.label }</label>
				}
				<div class={ styles.inputWrapperWrapper }>
					{ this.prefix
						&& <span class={ styles.inputWrapperPrefix }>{ this.prefix }</span>
					}
					{ this.$slots.default && this.$slots.default() }
					{ this.icon
						&& <div class={ styles.inputWrapperInfo }>
							<Icon class={ styles.inputWrapperIcon } name={ this.icon } />
						</div>
					}
					{ (this.clearable && this.onClear)
						? <Button type={ ButtonTypes.link } onClick={ this.onClear }
							class={ styles.inputWrapperClearable }>
							<Icon name={ IconNames.close }/>
						</Button>
						: '' }
				</div>
				{ this.isError
					&& <div class={ styles.inputWrapperMessage }>{ this.error }</div>
				}
			</div>
		);
	}
}
